/**
 * Cross-app / cross-tab workspace sync (WorkspaceMonitor pattern):
 * - Polls GET /api/workspace/ping — Bearer `selected_tenant` vs DB; when out of sync the server
 *   may return **`access_token` + `expires_in` inline** (fast path — no extra round-trip).
 * - Fallback: `signinSilent` + POST `/api/workspace/adopt` when inline token is absent (cross-app catch-up).
 *   **`/adopt` is not used after `POST /switch` in this app** — `switchWorkspace` stores the switch response token.
 * - **`monitorCurrentUser`** — source of truth for the next `/ping` Bearer; avoid trusting
 *   `getUser()` after renew (oidc storage key mismatch).
 * - BroadcastChannel + localStorage; no `location.reload()` in the refresh path.
 */
import offlineConfig from "../config/offlineMode";
import store from "../vuex/store";
import {
  getUserManager,
  applyNewTokenFromWorkspaceSwitch,
} from "../js/authentication/authentication";
import { workspaceService } from "./workspaceService";

const BROADCAST_CHANNEL_NAME = "ixp-workspace-channel";
const STORAGE_KEY_SWITCHED = "ixp-workspace-switched";
/** Old key for backward compatibility during rollout */
const STORAGE_KEY_SWITCHED_OLD = "workspace-switched";
const STORAGE_EVENT_MAX_AGE_MS = 10000;
const DEFAULT_POLL_MS = 5000;

/** Last interval used for setInterval — used to restart after applying /ping inline token. */
let activePollIntervalMs = DEFAULT_POLL_MS;
/** Prevents overlapping pollWorkspacePing runs (await ping allows next interval tick with stale Bearer). */
let pollCycleLock = false;

let pollTimerId = null;
let broadcastChannel = null;
let storageListenerBound = false;
let visibilityListenerBound = false;
let isRefreshing = false;
let marketDriftRefreshInFlight = false;
let marketDriftRetryBlockedUntil = 0;
const MARKET_DRIFT_RETRY_COOLDOWN_MS = 15000;
const TENANT_MARKET_PREFERENCE_GRACE_MS = 3000;
let lastExternalWorkspaceContextSignature = null;
let lastExternalWorkspaceContextAtMs = 0;
let lastCanonicalMarketApplySignature = null;
let lastCanonicalMarketApplyAtMs = 0;
const canonicalMarketApplyMetrics = {
  none: 0,
  unavailable: 0,
  aligned: 0,
  deduped: 0,
  applied: 0,
  journey_ingress_market_broadcast: 0,
  journey_ingress_market_storage: 0,
  journey_poll_started: 0,
  journey_poll_skipped_offline: 0,
  journey_poll_skipped_logged_out: 0,
  journey_poll_skipped_switch_in_progress: 0,
  journey_poll_skipped_cycle_lock: 0,
  journey_poll_skipped_refresh_in_progress: 0,
  journey_ping_in_sync: 0,
  journey_ping_needs_refresh: 0,
  journey_ping_market_hint_present: 0,
  journey_ping_market_hint_missing: 0,
  journey_market_drift_detected: 0,
  journey_market_drift_refresh_triggered: 0,
  journey_market_list_recovery_triggered: 0,
  journey_workspace_refresh_triggered: 0,
  journey_canonical_apply_recovery_retry: 0,
};

function publishCanonicalMarketApplyMetrics() {
  const snapshot = Object.assign({}, canonicalMarketApplyMetrics);
  try {
    if (typeof window !== "undefined") {
      window.__ixpCanonicalMarketApplyMetrics = snapshot;
    }
  } catch (e) {
    // Ignore window assignment failures in non-browser contexts.
  }
  try {
    if (typeof globalThis !== "undefined") {
      globalThis.__ixpCanonicalMarketApplyMetrics = snapshot;
    }
  } catch (e) {
    // Ignore global assignment failures in restricted contexts.
  }
}

publishCanonicalMarketApplyMetrics();
/** Log once if /ping says refresh but we cannot find access_token (wrong JSON shape). */
let warnedMissingPingAccessToken = false;

function recordCanonicalMarketApplyMetric(outcome) {
  if (!Object.prototype.hasOwnProperty.call(canonicalMarketApplyMetrics, outcome)) {
    return;
  }
  canonicalMarketApplyMetrics[outcome] += 1;
  publishCanonicalMarketApplyMetrics();
}

function recordJourneyMetric(name) {
  if (!Object.prototype.hasOwnProperty.call(canonicalMarketApplyMetrics, name)) {
    return;
  }
  canonicalMarketApplyMetrics[name] += 1;
  publishCanonicalMarketApplyMetrics();
}

/** Deduplicate concurrent POST /adopt (multi-tab / parallel refresh paths). */
let adoptInFlight = null;

/** LOOP FIX: Tenant protection window after /adopt or inline token apply (5000ms) **/
let protectedTenant = null;
let protectedUntil = 0;

/** Loop detection: track consecutive needsRefresh with same token prefix */
let consecutiveNeedsRefresh = 0;
let lastNeedsRefreshTokenPrefix = null;
const MAX_CONSECUTIVE_NEEDS_REFRESH = 15;

const IS_DEV = process.env.NODE_ENV === "development";
function debugLog() {
  if (!IS_DEV) return;
  console.log.apply(console, arguments);
}

function adoptWorkspaceDeduped(token) {
  if (adoptInFlight) {
    return adoptInFlight;
  }
  adoptInFlight = workspaceService.adoptWorkspace(token).finally(function () {
    adoptInFlight = null;
  });
  return adoptInFlight;
}

/** In-memory user for /ping — prefer over getUser() to avoid stale storage after signinSilent. */
let monitorCurrentUser = null;

/** True while POST /switch is in flight — skip /ping so we don't send a stale Bearer mid-switch. */
let workspaceSwitchInProgress = false;

/**
 * Pause workspace ping during user-initiated switch (race: poll can fire before applyNewToken finishes).
 * @param {boolean} v
 */
export function setWorkspaceSwitchInProgress(v) {
  workspaceSwitchInProgress = !!v;
}

function parseJwtPayloadForWorkspace(accessToken) {
  try {
    return JSON.parse(atob(accessToken.split(".")[1] || "e30="));
  } catch (e) {
    return {};
  }
}

function workspaceSwitchedAtFromAccessToken(accessToken) {
  var p = parseJwtPayloadForWorkspace(accessToken);
  if (p && p.workspace_switched_at != null) {
    var n = Number(p.workspace_switched_at);
    return isNaN(n) ? null : n;
  }
  return null;
}

function iatFromAccessToken(accessToken) {
  var p = parseJwtPayloadForWorkspace(accessToken);
  if (p && p.iat != null) {
    var n = Number(p.iat);
    return isNaN(n) ? 0 : n;
  }
  return 0;
}

function isIncomingTokenOlder(currentToken, incomingToken) {
  var currentWsa = workspaceSwitchedAtFromAccessToken(currentToken);
  var incomingWsa = workspaceSwitchedAtFromAccessToken(incomingToken);
  if (currentWsa != null && incomingWsa != null && incomingWsa !== currentWsa) {
    return incomingWsa < currentWsa;
  }
  if (currentWsa != null && incomingWsa == null) return true;
  if (currentWsa == null && incomingWsa != null) return false;

  var currentIat = iatFromAccessToken(currentToken);
  var incomingIat = iatFromAccessToken(incomingToken);
  return incomingIat < currentIat;
}

/**
 * Choose the freshest user for /ping.
 * Prefer higher workspace_switched_at, then iat when workspace context is equal/unknown.
 * @param {import("oidc-client").User | null} monitorUser
 * @param {import("oidc-client").User | null} storageUser
 * @returns {{ user: import("oidc-client").User | null }}
 */
function pickUserForPing(monitorUser, storageUser) {
  var monTok = monitorUser && monitorUser.access_token;
  var stoTok = storageUser && storageUser.access_token;

  if (!monTok && !stoTok) return { user: null };
  if (!monTok) return { user: storageUser || null };
  if (!stoTok) return { user: monitorUser || null };

  var wMon = workspaceSwitchedAtFromAccessToken(monTok);
  var wSto = workspaceSwitchedAtFromAccessToken(stoTok);
  if (wMon != null && wSto != null && wMon !== wSto) {
    return { user: wMon > wSto ? monitorUser : storageUser };
  }
  if (wMon != null && wSto == null) return { user: monitorUser };
  if (wSto != null && wMon == null) return { user: storageUser };

  var tMon = selectedTenantFromAccessTokenJwt(monTok);
  var tSto = selectedTenantFromAccessTokenJwt(stoTok);
  if (tMon && tSto && tMon !== tSto) {
    // No decisive workspace_switched_at and tenants differ: prefer newer iat to avoid
    // pinning /ping to a stale monitor token.
    var iMonD = iatFromAccessToken(monTok);
    var iStoD = iatFromAccessToken(stoTok);
    return { user: iMonD >= iStoD ? monitorUser : storageUser };
  }

  var iMon = iatFromAccessToken(monTok);
  var iSto = iatFromAccessToken(stoTok);
  return { user: iMon >= iSto ? monitorUser : storageUser };
}

/**
 * OIDC `userLoaded` can fire with a stale access_token (e.g. refresh completed after /switch or /adopt
 * already applied a newer JWT to monitor). If we overwrite monitor with that stale user, /ping keeps
 * sending the old Bearer — matches server logs (token_tenant stuck, loop with /adopt).
 * @param {import("oidc-client").User} loadedUser
 * @returns {boolean} true = keep current monitor, do not setMonitorCurrentUser(loadedUser)
 */
export function shouldPreferMonitorOverLoadedUser(loadedUser) {
  const monitor = monitorCurrentUser;
  if (!loadedUser || !loadedUser.access_token) return false;
  if (!monitor || !monitor.access_token) return false;
  if (monitor.access_token === loadedUser.access_token) return false;

  // LOOP FIX: During protection window after /adopt, REJECT users with different tenant
  const loadedTenant = selectedTenantFromAccessTokenJwt(loadedUser.access_token);
  const monitorTenant = selectedTenantFromAccessTokenJwt(monitor.access_token);
  if (protectedTenant && Date.now() < protectedUntil) {
    if (loadedTenant !== protectedTenant) {
      debugLog(
        "[WorkspaceSync] shouldPreferMonitorOverLoadedUser: REJECTING stale userLoaded during protection window",
        { protectedTenant, loadedTenant, protectedUntilMs: protectedUntil - Date.now() }
      );
      return true;  // Keep monitor
    }
  }

  const m = parseJwtPayloadForWorkspace(monitor.access_token);
  const l = parseJwtPayloadForWorkspace(loadedUser.access_token);
  const mt = monitorTenant;
  const lt = loadedTenant;
  const mws =
    m.workspace_switched_at != null ? Number(m.workspace_switched_at) : null;
  const lws =
    l.workspace_switched_at != null ? Number(l.workspace_switched_at) : null;
  const mi = m.iat != null ? Number(m.iat) : 0;
  const li = l.iat != null ? Number(l.iat) : 0;

  if (mt === lt) {
    return mi > li;
  }
  if (mws != null && lws != null) {
    return mws > lws;
  }
  if (mws != null && lws == null) {
    return true;
  }
  if (lws != null && mws == null) {
    return false;
  }
  // Both lack workspace_switched_at — do NOT use iat alone: OIDC refresh can mint a newer iat
  // while ProfileService still embeds the previous selected_tenant (server logs: token_tenant stuck).
  // Prefer the in-memory monitor set by /switch, /adopt, or /ping inline — never downgrade via userLoaded.
  return true;
}

/**
 * Set tenant protection window (5 seconds) after /adopt or inline token apply.
 * During this window, handleUserLoaded rejects stale OIDC events with different tenant.
 * @param {string} tenant
 * @param {number} [durationMs=5000]
 */
function setTenantProtection(tenant, durationMs = 5000) {
  protectedTenant = tenant;
  protectedUntil = Date.now() + durationMs;
  debugLog("[WorkspaceSync] setTenantProtection: protecting tenant", { tenant, durationMs });
}

/**
 * Clear tenant protection window. Called when /ping returns needsRefresh=false (sync complete).
 */
function clearTenantProtection() {
  if (protectedTenant) {
    debugLog("[WorkspaceSync] clearTenantProtection: clearing", { protectedTenant });
  }
  protectedTenant = null;
  protectedUntil = 0;
}

/**
 * Check loop detection. Increments counter if same token triggers needsRefresh again.
 * @param {string} tokenPrefix First 50 chars of bearer token
 * @returns {boolean} true if loop threshold exceeded
 */
function checkLoopDetection(tokenPrefix) {
  if (lastNeedsRefreshTokenPrefix === tokenPrefix) {
    consecutiveNeedsRefresh++;
    console.warn("[WorkspaceSync] LOOP DETECTED: Same token triggering needsRefresh", {
      consecutiveCount: consecutiveNeedsRefresh,
      tokenPrefix,
    });
    return consecutiveNeedsRefresh >= MAX_CONSECUTIVE_NEEDS_REFRESH;
  } else {
    consecutiveNeedsRefresh = 1;
    lastNeedsRefreshTokenPrefix = tokenPrefix;
    return false;
  }
}

/**
 * Reset loop detection. Called when /ping returns needsRefresh=false.
 */
function resetLoopDetection() {
  consecutiveNeedsRefresh = 0;
  lastNeedsRefreshTokenPrefix = null;
}

function selectedTenantFromAccessTokenJwt(accessToken) {
  if (!accessToken || typeof accessToken !== "string") return "";
  try {
    const payload = JSON.parse(atob(accessToken.split(".")[1] || "e30="));
    const t =
      payload.selected_tenant != null
        ? payload.selected_tenant
        : payload.selectedTenantId != null
          ? payload.selectedTenantId
          : "";
    return t != null ? String(t).trim() : "";
  } catch (e) {
    return "";
  }
}

/**
 * Merge a new access_token onto a user object for in-memory /ping (oidc getUser() may still
 * return the pre-adopt token from storage — same as spec's `{ ...user, access_token }`).
 */
function mergeUserWithAdoptedAccessToken(baseUser, accessToken, expiresIn) {
  var expSec = expiresIn != null ? Number(expiresIn) : 120;
  if (isNaN(expSec) || expSec <= 0) {
    expSec = 120;
  }
  var expAt = Math.floor(Date.now() / 1000) + expSec;
  var merged = Object.assign({}, baseUser || {}, {
    access_token: accessToken,
    expires_in: expSec,
    expires_at: expAt,
  });
  try {
    var pl = JSON.parse(atob(accessToken.split(".")[1] || "e30="));
    merged.profile = Object.assign({}, (baseUser && baseUser.profile) || {}, pl);
  } catch (e) {
    /* ignore */
  }
  return merged;
}

/**
 * Read corrected JWT from /ping JSON (.NET may use PascalCase or nested token object).
 */
function getAccessTokenFromPingBody(data) {
  if (!data || typeof data !== "object") return null;
  var t =
    data.access_token != null
      ? data.access_token
      : data.accessToken != null
        ? data.accessToken
        : data.AccessToken != null
          ? data.AccessToken
          : data.token != null
            ? data.token
            : data.Token != null
              ? data.Token
              : null;
  if (t != null && typeof t === "object" && t.access_token != null) {
    t = t.access_token;
  }
  if (t != null && typeof t === "string" && String(t).trim() !== "") {
    return String(t).trim();
  }
  return null;
}

/** True when /ping says the client should refresh (camelCase or PascalCase, bool or string). */
function pingSaysNeedsRefresh(data) {
  if (!data || typeof data !== "object") return false;
  if (data.needsRefresh === true || data.NeedsRefresh === true) return true;
  if (data.needsRefresh === "true" || data.NeedsRefresh === "true") return true;
  return false;
}

/** Read `selected_market` from a JWT access token payload. Returns "" when absent or unparseable. */
function getSelectedMarketFromToken(accessToken) {
  if (!accessToken || typeof accessToken !== "string") return "";
  try {
    var payload = JSON.parse(atob(accessToken.split(".")[1] || "e30="));
    var m = payload.selected_market;
    return m != null ? String(m).trim() : "";
  } catch (e) {
    return "";
  }
}

function normalizeMarketId(value) {
  if (value == null) return "";
  return String(value).trim().toLowerCase();
}

function getCurrentTenantIdForMarketSync() {
  var workspaceTenant =
    store.state.workspace && store.state.workspace.currentTenantId != null
      ? String(store.state.workspace.currentTenantId).trim()
      : "";
  if (workspaceTenant) return workspaceTenant;

  var profile = store.state.authentication.user && store.state.authentication.user.profile;
  var profileTenant =
    profile && profile.selected_tenant != null ? String(profile.selected_tenant).trim() : "";
  return profileTenant || "";
}

function getTenantStoredMarketId(tenantId) {
  if (!tenantId) return "";
  try {
    // After simplification, read directly from Vuex store instead of session storage
    const currentTenantId = store?.state?.workspace?.currentTenantId || 
                           store?.state?.authentication?.user?.profile?.selected_tenant || null;
    if (!currentTenantId || currentTenantId !== tenantId) return "";
    
    const marketId = store?.state?.assetxpstate?.selectedMarketId || null;
    return marketId ? String(marketId).trim() : "";
  } catch (e) {
    return "";
  }
}

function getTenantStoredMarketUpdatedAtMs(tenantId) {
  if (!tenantId) return 0;
  try {
    // After simplification, read directly from Vuex store instead of session storage
    const currentTenantId = store?.state?.workspace?.currentTenantId || 
                           store?.state?.authentication?.user?.profile?.selected_tenant || null;
    if (!currentTenantId || currentTenantId !== tenantId) return 0;
    
    const changedAtMs = Number(store?.state?.assetxpstate?.selectedMarketUserChangedAtMs || 0);
    return Number.isFinite(changedAtMs) && changedAtMs > 0 ? changedAtMs : 0;
  } catch (e) {
    return 0;
  }
}

function shouldDeferToTenantStoredMarket(authoritativeMarketId) {
  var normalizedAuthoritative = normalizeMarketId(authoritativeMarketId);
  if (!normalizedAuthoritative) {
    return { shouldDefer: false, tenantStoredMarketId: "", tenantId: "" };
  }

  var tenantId = getCurrentTenantIdForMarketSync();
  if (!tenantId) {
    return { shouldDefer: false, tenantStoredMarketId: "", tenantId: "" };
  }

  var tenantStoredMarketId = getTenantStoredMarketId(tenantId);
  var tenantStoredMarketUpdatedAtMs = getTenantStoredMarketUpdatedAtMs(tenantId);
  var normalizedTenantStored = normalizeMarketId(tenantStoredMarketId);
  if (!normalizedTenantStored) {
    return {
      shouldDefer: false,
      tenantStoredMarketId: "",
      tenantStoredMarketUpdatedAtMs: 0,
      tenantStoredSelectionIsFresh: false,
      tenantId,
    };
  }

  var tenantStoredSelectionIsFresh =
    tenantStoredMarketUpdatedAtMs > 0 &&
    Date.now() - tenantStoredMarketUpdatedAtMs <= TENANT_MARKET_PREFERENCE_GRACE_MS;

  return {
    shouldDefer:
      tenantStoredSelectionIsFresh &&
      normalizedTenantStored !== normalizedAuthoritative,
    tenantStoredMarketId,
    tenantStoredMarketUpdatedAtMs,
    tenantStoredSelectionIsFresh,
    tenantId,
  };
}

/** Read `selectedMarketId` from the /ping response body (camelCase or PascalCase). */
function getSelectedMarketIdFromPingBody(data) {
  if (!data || typeof data !== "object") return null;
  var m =
    data.selectedMarketId != null ? data.selectedMarketId
    : data.SelectedMarketId != null ? data.SelectedMarketId
    : null;
  return m != null && typeof m === "string" && m.trim() !== "" ? m.trim() : null;
}

function emitWorkspaceContextChanged(detail, options) {
  // BUG FIX: Default appSource to "assetxp" only if not explicitly provided
  // Empty or unknown sources should be preserved to signal external/unknown apps
  var appSourceInput = detail && detail.appSource;
  var normalizedAppSource = "";
  if (typeof appSourceInput === "string" && appSourceInput.trim() !== "") {
    normalizedAppSource = appSourceInput.trim();
  } else if (appSourceInput === "") {
    normalizedAppSource = ""; // Preserve empty string to indicate malformed event
  } else {
    normalizedAppSource = "assetxp"; // Default only if not provided
  }

  var eventDetail = Object.assign(
    {
      workspaceChanged: false,
      marketChanged: false,
      selectedTenantId: null,
      selectedMarketId: null,
      source: "unknown",
      appSource: normalizedAppSource,
    },
    detail || {}
  );
  // Override with normalized appSource to ensure consistent handling
  eventDetail.appSource = normalizedAppSource;

  var dedupeMs = options && typeof options.dedupeMs === "number" ? options.dedupeMs : 0;
  var externalContext = !!(options && options.externalContext);
  if (externalContext) {
    eventDetail.externalContext = true;
  }

  if (dedupeMs > 0) {
    var sig = [
      eventDetail.workspaceChanged ? "w1" : "w0",
      eventDetail.marketChanged ? "m1" : "m0",
      eventDetail.source || "unknown",
      eventDetail.selectedTenantId || "",
      eventDetail.selectedMarketId || "",
    ].join("|");
    var now = Date.now();
    if (
      lastExternalWorkspaceContextSignature === sig &&
      now - lastExternalWorkspaceContextAtMs < dedupeMs
    ) {
      return false;
    }
    lastExternalWorkspaceContextSignature = sig;
    lastExternalWorkspaceContextAtMs = now;
  }

  window.dispatchEvent(
    new CustomEvent("workspace-context-changed", {
      detail: eventDetail,
    })
  );
  return true;
}

async function applyCanonicalMarketContext(marketId, source, appSource, selectedTenantId) {
  if (!marketId || typeof marketId !== "string") {
    recordCanonicalMarketApplyMetric("none");
    return false;
  }
  var canonicalMarketId = marketId.trim();
  if (!canonicalMarketId) {
    recordCanonicalMarketApplyMetric("none");
    return false;
  }

  var availableMarkets =
    store.state.assetxpstate && Array.isArray(store.state.assetxpstate.markets)
      ? store.state.assetxpstate.markets
      : [];
  var normalizedCanonical = normalizeMarketId(canonicalMarketId);
  var marketIsAvailable = availableMarkets.some(
    (m) => m && normalizeMarketId(m.id) === normalizedCanonical
  );
  if (!marketIsAvailable) {
    recordCanonicalMarketApplyMetric("unavailable");
    return false;
  }

  var currentSelected =
    store.state.assetxpstate && typeof store.state.assetxpstate.selectedMarketId === "string"
      ? store.state.assetxpstate.selectedMarketId.trim()
      : "";
  if (normalizeMarketId(currentSelected) === normalizedCanonical) {
    recordCanonicalMarketApplyMetric("aligned");
    return true;
  }

  var tenantForSig = selectedTenantId || getCurrentTenantIdForMarketSync() || "";
  var applySignature = [tenantForSig, canonicalMarketId].join("|");
  var now = Date.now();
  if (
    lastCanonicalMarketApplySignature === applySignature &&
    now - lastCanonicalMarketApplyAtMs < 4000
  ) {
    recordCanonicalMarketApplyMetric("deduped");
    return true;
  }
  lastCanonicalMarketApplySignature = applySignature;
  lastCanonicalMarketApplyAtMs = now;

  store.commit("assetxpstate/SetSelectedMarket", {
    marketId: canonicalMarketId,
    source: "external-market-sync",
  });
  await store.dispatch("authentication/updateMarketPermissions");

  emitWorkspaceContextChanged({
    workspaceChanged: false,
    marketChanged: true,
    selectedTenantId: selectedTenantId || null,
    selectedMarketId: canonicalMarketId,
    source: source || "poll",
    appSource: appSource || "assetxp",
  });

  recordCanonicalMarketApplyMetric("applied");

  // Note: BroadcastChannel and localStorage won't work across ports (different origins).
  // Cross-app sync relies on polling. Portal will detect this change in next /ping cycle.

  return true;
}

async function applyCanonicalMarketContextWithRecovery(marketId, source, appSource, selectedTenantId) {
  const applied = await applyCanonicalMarketContext(marketId, source, appSource, selectedTenantId);
  if (applied) {
    return true;
  }

  const marketIdStr = marketId != null ? String(marketId).trim() : "";
  if (!marketIdStr) {
    return false;
  }

  recordJourneyMetric("journey_canonical_apply_recovery_retry");
  try {
    await store.dispatch("assetxpstate/GetMyMarkets");
  } catch (e) {
    // Ignore refresh failures; fallback retry will no-op if market list remains unavailable.
  }

  return applyCanonicalMarketContext(marketIdStr, source, appSource, selectedTenantId);
}

/**
 * Another app changed the selected market in the DB. Do a silent refresh to get the new
 * `selected_market` claim, then reload the market list — no navigation, no state clear.
 */
async function refreshMarketAfterDrift(authoritativeMarketIdHint) {
  if (marketDriftRefreshInFlight) return;
  // Do not race with a workspace refresh — workspace refresh also calls signinSilent and may
  // overwrite monitorCurrentUser; market drift will be re-evaluated on the next poll cycle.
  if (isRefreshing) {
    debugLog("[WorkspaceSync] refreshMarketAfterDrift skipped — workspace refresh in flight");
    return;
  }
  if (Date.now() < marketDriftRetryBlockedUntil) return;
  marketDriftRefreshInFlight = true;
  try {
    var userManager = getUserManager();
    if (!userManager || typeof userManager.signinSilent !== "function") return;
    var renewed = false;
    try {
      var newUser = await userManager.signinSilent();
      if (newUser && newUser.access_token) {
        monitorCurrentUser = newUser;
        renewed = true;
      }
    } catch (e) {
      debugLog("[WorkspaceSync] signinSilent after market drift failed:", e);
      marketDriftRetryBlockedUntil = Date.now() + MARKET_DRIFT_RETRY_COOLDOWN_MS;
      return;
    }

    if (!renewed) return;

    await store.dispatch("authentication/updateLoggedInStatus");
    await store.dispatch("assetxpstate/GetMyMarkets");

    var selectedFromToken =
      newUser && newUser.profile && typeof newUser.profile.selected_market === "string"
        ? newUser.profile.selected_market.trim()
        : "";
    var authoritativeMarketId =
      authoritativeMarketIdHint != null && String(authoritativeMarketIdHint).trim() !== ""
        ? String(authoritativeMarketIdHint).trim()
        : "";

    var availableMarketIds =
      store.state.assetxpstate && Array.isArray(store.state.assetxpstate.markets)
        ? store.state.assetxpstate.markets
            .map((m) => (m && m.id ? String(m.id).trim() : ""))
            .filter(Boolean)
        : [];

    var targetMarketId = "";
    if (authoritativeMarketId && availableMarketIds.includes(authoritativeMarketId)) {
      // Prefer authoritative DB market from /ping when available.
      targetMarketId = authoritativeMarketId;
    } else if (selectedFromToken && availableMarketIds.includes(selectedFromToken)) {
      targetMarketId = selectedFromToken;
    }

    if (targetMarketId) {
      var currentSelected =
        store.state.assetxpstate && typeof store.state.assetxpstate.selectedMarketId === "string"
          ? store.state.assetxpstate.selectedMarketId.trim()
          : "";
      if (currentSelected !== targetMarketId) {
        var tenantPreference = shouldDeferToTenantStoredMarket(targetMarketId);
        if (tenantPreference.shouldDefer) {
          debugLog("[WorkspaceSync] refreshMarketAfterDrift deferred to tenant-stored market", {
            tenantId: tenantPreference.tenantId,
            dbMarketId: targetMarketId,
            tenantStoredMarketId: tenantPreference.tenantStoredMarketId,
            tenantStoredMarketUpdatedAtMs:
              tenantPreference.tenantStoredMarketUpdatedAtMs || null,
            tenantStoredSelectionIsFresh:
              !!tenantPreference.tenantStoredSelectionIsFresh,
          });
          return;
        }

        const applied = await applyCanonicalMarketContextWithRecovery(
          targetMarketId,
          "poll",
          "assetxp",
          getCurrentTenantIdForMarketSync()
        );
        if (!applied) {
          return;
        }

      }
    }
  } finally {
    marketDriftRefreshInFlight = false;
  }
}

function getExpiresInFromPingBody(data) {
  if (!data || typeof data !== "object") return null;
  if (data.expires_in != null) return data.expires_in;
  if (data.expiresIn != null) return data.expiresIn;
  if (data.ExpiresIn != null) return data.ExpiresIn;
  return null;
}

function dispatchWorkspaceChangedIfNeeded(reason, hint, selectedTenantId, selectedMarketId) {
  function dispatchExternalWorkspaceContextChanged(detail) {
    emitWorkspaceContextChanged(detail, {
      externalContext: true,
      dedupeMs: 10000,
    });
  }

  var nextStr =
    selectedTenantId != null ? String(selectedTenantId).trim() : "";
  if (nextStr !== "") {
    var wsId =
      store.state.workspace && store.state.workspace.currentTenantId != null
        ? String(store.state.workspace.currentTenantId).trim()
        : "";
    if (wsId !== "" && wsId === nextStr) {
      var storeSelectedMarketId =
        store.state.assetxpstate && typeof store.state.assetxpstate.selectedMarketId === "string"
          ? store.state.assetxpstate.selectedMarketId.trim()
          : "";
      var marketListCount =
        store.state.assetxpstate && Array.isArray(store.state.assetxpstate.markets)
          ? store.state.assetxpstate.markets.length
          : 0;
      var hintMarketId =
        selectedMarketId != null ? String(selectedMarketId).trim() : "";
      var normalizedStoreMarket = normalizeMarketId(storeSelectedMarketId);
      var normalizedHintMarket = normalizeMarketId(hintMarketId);

      // If we are already on the target workspace, avoid re-dispatching workspace-changed popup.
      // Single-tenant users on initial login will have marketListCount === 0, so we must skip
      // the popup trigger even without a loaded market list — no actual workspace switch occurred.
      // For multi-tenant users: only skip if market list is loaded (marketListCount > 0) since
      // we may need to reload markets for the workspace.
      var skipWorkspaceEventForSingleTenant = false;
      if (marketListCount === 0) {
        // No markets loaded yet. For a single-tenant user logging in, this is normal on first load.
        // We'll trigger market load, but don't show the "workspace switched" popup.
        // Check if user has only one client/tenant available (single-tenant scenario).
        var availableClients =
          store.state.workspace && Array.isArray(store.state.workspace.availableClients)
            ? store.state.workspace.availableClients.length
            : 0;
        skipWorkspaceEventForSingleTenant = availableClients <= 1;
        if (skipWorkspaceEventForSingleTenant) {
          debugLog("[WorkspaceSync] single-tenant user on initial login — skipping workspace-changed event", {
            selectedTenantId: nextStr,
            availableClients,
            reason: reason || "poll",
          });
          // Still load markets if needed
          void store.dispatch("assetxpstate/GetMyMarkets");
          return;
        }
      }

      if (marketListCount > 0) {
        if (normalizedHintMarket && normalizedHintMarket !== normalizedStoreMarket) {
          void applyCanonicalMarketContextWithRecovery(
            hintMarketId,
            reason || "poll",
            "assetxp",
            nextStr
          );
          debugLog("[WorkspaceSync] applied same-tenant market hint without workspace refresh", {
            selectedTenantId: nextStr,
            selectedMarketId: hintMarketId,
            previousMarketId: storeSelectedMarketId,
            reason: reason || "poll",
          });
          return;
        }

        debugLog(
          "[WorkspaceSync] skipping workspace-changed dispatch — already aligned",
          {
            selectedTenantId: nextStr,
            storeSelectedMarketId,
            selectedMarketId: hintMarketId || null,
            marketListCount,
            reason: reason || "poll",
          }
        );
        return;
      }

      debugLog(
        "[WorkspaceSync][v2-force-refresh] UI already on workspace",
        nextStr,
        "— forcing workspace-changed refresh",
        {
          selectedMarketId: hintMarketId,
          storeSelectedMarketId,
          marketListCount,
          reason: reason || "poll",
        }
      );
    }
  }

  window.dispatchEvent(
    new CustomEvent("workspace-changed", {
      detail: {
        reason: reason,
        selectedTenantId: selectedTenantId,
        correctTenantId: hint || null,
        selectedMarketId: selectedMarketId || null,
      },
    })
  );

  emitWorkspaceContextChanged(
    {
      workspaceChanged: true,
      marketChanged: false,
      selectedTenantId: selectedTenantId,
      selectedMarketId: selectedMarketId || null,
      source: reason || "poll",
      appSource: "assetxp",
    },
    {
      externalContext: true,
    }
  );
}

/**
 * Identity Server: when `needsRefresh: true`, `/ping` may include corrected `access_token` — apply
 * immediately so the next poll sends the right Bearer (no signinSilent loop).
 */
async function applyPingInlineAccessToken(pingToken, pingExp, hint, reason) {
  var exp =
    pingExp != null && !isNaN(Number(pingExp)) ? Number(pingExp) : 120;
  await applyNewTokenFromWorkspaceSwitch(pingToken, exp);
  var merged = mergeUserWithAdoptedAccessToken(
    monitorCurrentUser,
    pingToken,
    exp
  );
  monitorCurrentUser = merged;
  
  // LOOP FIX: Set protection to prevent stale userLoaded events from overwriting
  const inlineTenant = selectedTenantFromAccessTokenJwt(pingToken);
  if (inlineTenant) {
    setTenantProtection(inlineTenant);
  }

  var userManager = getUserManager();
  if (userManager && typeof userManager.storeUser === "function") {
    try {
      var stored = await userManager.getUser();
      if (stored && typeof stored === "object") {
        stored.access_token = merged.access_token;
        stored.expires_at = merged.expires_at;
        stored.expires_in = merged.expires_in;
        if (merged.profile) {
          stored.profile = merged.profile;
        }
        userManager.storeUser(stored);
      } else {
        userManager.storeUser(merged);
      }
    } catch (storeErr) {
      debugLog("[WorkspaceSync] storeUser after /ping inline token failed:", storeErr);
    }
  }

  await store.dispatch("authentication/updateLoggedInStatus");

  var fromJwt = selectedTenantFromAccessTokenJwt(pingToken);
  var selectedTenantId = null;
  if (hint && String(hint).trim() !== "") {
    selectedTenantId = String(hint).trim();
  } else if (fromJwt) {
    selectedTenantId = fromJwt;
  }

  dispatchWorkspaceChangedIfNeeded(reason, hint || undefined, selectedTenantId, null);
}

/**
 * Clear and recreate the poll timer so the next tick cannot fire while the previous cycle
 * was still awaiting; optionally schedule an immediate poll after the current stack clears.
 */
function restartPollIntervalAfterTokenUpdate() {
  if (pollTimerId != null) {
    clearInterval(pollTimerId);
    pollTimerId = null;
  }
  pollTimerId = window.setInterval(pollWorkspacePing, activePollIntervalMs);
  window.setTimeout(function () {
    pollWorkspacePing();
  }, 0);
}

/**
 * Silent renew + optional POST /adopt + Vuex sync + workspace-changed. No reload, no POST /switch here.
 * When signinSilent() still yields a JWT whose selected_tenant ≠ DB (storage key mismatch), /adopt
 * returns a fresh access token — no cooldown (unlike /switch).
 * @param {string} [authoritativeTenantIdHint] — `correctTenantId` from /ping or broadcast tenant id
 */
async function refreshTokenAfterWorkspaceDrift(
  reason,
  authoritativeTenantIdHint,
  authoritativeMarketIdHint
) {
  if (isRefreshing) return;
  isRefreshing = true;
  const userManager = getUserManager();
  const hint =
    authoritativeTenantIdHint != null &&
    String(authoritativeTenantIdHint).trim() !== ""
      ? String(authoritativeTenantIdHint).trim()
      : "";

  try {
    if (!userManager || typeof userManager.signinSilent !== "function") {
      return;
    }

    var newUser = null;
    try {
      newUser = await userManager.signinSilent();
    } catch (silentErr) {
      debugLog("[WorkspaceSync] signinSilent failed:", silentErr);
    }

    // CRITICAL FIX: Use newUser directly from signinSilent - do NOT read from storage.
    // Storage read can race and return stale data before the write commits (IndexedDB async).
    // This matches the IXPOnlinePortal fix for the same race condition.
    if (newUser && newUser.access_token) {
      var monitorToken =
        monitorCurrentUser && monitorCurrentUser.access_token
          ? String(monitorCurrentUser.access_token)
          : "";
      if (monitorToken && isIncomingTokenOlder(monitorToken, newUser.access_token)) {
        debugLog("[WorkspaceSync] signinSilent returned stale token; keeping monitor token", {
          currentTenant: selectedTenantFromAccessTokenJwt(monitorToken) || null,
          incomingTenant: selectedTenantFromAccessTokenJwt(newUser.access_token) || null,
          currentWorkspaceSwitchedAt: workspaceSwitchedAtFromAccessToken(monitorToken),
          incomingWorkspaceSwitchedAt: workspaceSwitchedAtFromAccessToken(newUser.access_token),
          currentIat: iatFromAccessToken(monitorToken),
          incomingIat: iatFromAccessToken(newUser.access_token),
        });
      } else {
        monitorCurrentUser = newUser;
      }
      const tenant = selectedTenantFromAccessTokenJwt(newUser.access_token);
      // LOOP FIX: Set protection to prevent stale userLoaded events from overwriting
      if (tenant) {
        setTenantProtection(tenant);
      }
      debugLog("[WorkspaceSync] signinSilent success, using newUser directly", {
        tenant,
      });
    }

    // Use the user we already have - don't read from storage
    var effectiveUser = newUser || monitorCurrentUser;

    var jwtTenant = effectiveUser
      ? selectedTenantFromAccessTokenJwt(effectiveUser.access_token)
      : "";
    var needsAdopt =
      hint &&
      (!effectiveUser ||
        !effectiveUser.access_token ||
        !jwtTenant ||
        jwtTenant !== hint);

    if (needsAdopt) {
      var tokenForAdopt =
        (effectiveUser && effectiveUser.access_token) ||
        (monitorCurrentUser && monitorCurrentUser.access_token);
      if (!tokenForAdopt) {
        console.error("[WorkspaceSync] No token for /adopt — redirecting to login");
        await userManager.signinRedirect();
        return;
      }
      debugLog(
        "[WorkspaceSync] access_token selected_tenant still",
        jwtTenant || "(none)",
        "after signinSilent; calling POST /api/workspace/adopt for",
        hint
      );
      const adoptResult = await adoptWorkspaceDeduped(tokenForAdopt);
      if (adoptResult && adoptResult.notModified === true) {
        // 304: workspace unchanged since last adopt — stored token is already correct (per server).
        // CRITICAL FIX: Do NOT read from storage (race condition).
        // Keep existing effectiveUser/monitorCurrentUser as-is since 304 means "unchanged".
        debugLog("[WorkspaceSync] /adopt 304 — keeping current token");
        // effectiveUser stays as-is
      } else {
        var adoptAt =
          adoptResult &&
          (adoptResult.access_token != null
            ? adoptResult.access_token
            : adoptResult.accessToken);
        if (adoptAt) {
          var adoptExp =
            adoptResult.expires_in != null
              ? adoptResult.expires_in
              : adoptResult.expiresIn;
          await applyNewTokenFromWorkspaceSwitch(adoptAt, adoptExp);
          // Critical: do NOT assign monitorCurrentUser from getUser() here — oidc storage often
          // still holds the pre-adopt token, so the next /ping would send the wrong Bearer. Use
          // the JWT /adopt returned, synchronously (applyNewToken also schedules setMonitor async).
          effectiveUser = mergeUserWithAdoptedAccessToken(
            effectiveUser || monitorCurrentUser,
            adoptAt,
            adoptExp
          );
          monitorCurrentUser = effectiveUser;
          const adoptedTenant = selectedTenantFromAccessTokenJwt(effectiveUser.access_token);
          // LOOP FIX: Set protection to prevent stale userLoaded events from overwriting
          if (adoptedTenant) {
            setTenantProtection(adoptedTenant);
          }
          debugLog("[WorkspaceSync] /adopt success, using merged user directly", {
            tenant: adoptedTenant,
          });
        } else {
          console.error("[WorkspaceSync] /adopt failed — redirecting to login");
          await userManager.signinRedirect();
          return;
        }
      }
    }

    await store.dispatch("authentication/updateLoggedInStatus");

    var fromProfile =
      effectiveUser && effectiveUser.profile && effectiveUser.profile.selected_tenant != null
        ? String(effectiveUser.profile.selected_tenant).trim()
        : "";
    var fromJwt = effectiveUser
      ? selectedTenantFromAccessTokenJwt(effectiveUser.access_token)
      : "";
    var selectedTenantId = fromProfile || fromJwt || null;

    if (hint && (!selectedTenantId || selectedTenantId !== hint)) {
      selectedTenantId = hint;
    }

    const selectedMarketIdFromHint =
      authoritativeMarketIdHint != null &&
      String(authoritativeMarketIdHint).trim() !== ""
        ? String(authoritativeMarketIdHint).trim()
        : null;
    const selectedMarketIdFromToken =
      effectiveUser && effectiveUser.access_token
        ? getSelectedMarketFromToken(effectiveUser.access_token)
        : "";
    const selectedMarketId =
      selectedMarketIdFromHint ||
      (selectedMarketIdFromToken && selectedMarketIdFromToken.trim() !== ""
        ? selectedMarketIdFromToken.trim()
        : null);

    dispatchWorkspaceChangedIfNeeded(reason, hint || undefined, selectedTenantId, selectedMarketId);
  } catch (err) {
    console.error("[WorkspaceSync] Workspace refresh failed:", err);
    if (userManager && typeof userManager.signinRedirect === "function") {
      await userManager.signinRedirect();
    }
  } finally {
    isRefreshing = false;
  }
}

async function pollWorkspacePing() {
  recordJourneyMetric("journey_poll_started");
  if (offlineConfig.isOfflineMode && offlineConfig.bypassAuth) {
    recordJourneyMetric("journey_poll_skipped_offline");
    return;
  }
  if (!store.state.authentication.loggedIn) {
    recordJourneyMetric("journey_poll_skipped_logged_out");
    return;
  }
  if (workspaceSwitchInProgress) {
    recordJourneyMetric("journey_poll_skipped_switch_in_progress");
    return;
  }
  if (pollCycleLock) {
    recordJourneyMetric("journey_poll_skipped_cycle_lock");
    return;
  }
  if (isRefreshing) {
    recordJourneyMetric("journey_poll_skipped_refresh_in_progress");
    return;
  }

  pollCycleLock = true;
  try {
    const userManager = getUserManager();
    if (!userManager || typeof userManager.getUser !== "function") return;

    var storageUser = await userManager.getUser();
    var picked = pickUserForPing(monitorCurrentUser, storageUser);
    var user = picked.user;
    if (!user || !user.access_token) return;
    monitorCurrentUser = user;

    // Do not send profile.selected_tenant here — id_token profile can lag the access_token JWT and
    // produced misleading URLs (e.g. old tenant in query while Bearer is authoritative). Server
    // compares **Bearer JWT** to DB; currentTenantId query is legacy only — send empty.
    const data = await workspaceService.pingWorkspace(user.access_token, "");
    if (!data || !pingSaysNeedsRefresh(data)) {
      recordJourneyMetric("journey_ping_in_sync");
      // Sync is complete — reset loop detection and clear tenant protection
      resetLoopDetection();
      clearTenantProtection();
      // Check for market drift: another app may have changed selected_market in the DB.
      if (data) {
        var needsMarketListRecovery = false;
        var dbMarketId = getSelectedMarketIdFromPingBody(data);
        if (dbMarketId != null) {
          recordJourneyMetric("journey_ping_market_hint_present");
        } else {
          recordJourneyMetric("journey_ping_market_hint_missing");
        }
        if (dbMarketId != null) {
          var tokenMarket = getSelectedMarketFromToken(user.access_token);
          var tokenMarketId = normalizeMarketId(tokenMarket);
          var dbMarketIdNormalized = normalizeMarketId(dbMarketId);
            var storeSelectedForDrift =
              store.state.assetxpstate && typeof store.state.assetxpstate.selectedMarketId === "string"
                ? store.state.assetxpstate.selectedMarketId.trim()
                : "";
            var storeSelectedForDriftNormalized = normalizeMarketId(storeSelectedForDrift);
          if (tokenMarketId !== "" && tokenMarketId !== dbMarketIdNormalized) {
              if (
                storeSelectedForDriftNormalized &&
                storeSelectedForDriftNormalized === dbMarketIdNormalized
              ) {
                debugLog(
                  "[WorkspaceSync] market drift token mismatch ignored — store already aligned with DB market",
                  { tokenMarket, dbMarketId, storeSelectedMarketId: storeSelectedForDrift }
                );
                needsMarketListRecovery = true;
              } else {
            recordJourneyMetric("journey_market_drift_detected");
            debugLog("[WorkspaceSync] market drift detected — refreshing token", { tokenMarket, dbMarketId });
            recordJourneyMetric("journey_market_drift_refresh_triggered");
            void refreshMarketAfterDrift(dbMarketId);
              }
          } else if (tokenMarketId === "") {
            // Token missing selected_market claim; renew to recover canonical market context.
            recordJourneyMetric("journey_market_drift_refresh_triggered");
            void refreshMarketAfterDrift(dbMarketId);
          } else {
            needsMarketListRecovery = true;

            // In-sync token/DB can still leave stale local selected market in Vuex.
            var storeSelected =
              store.state.assetxpstate && typeof store.state.assetxpstate.selectedMarketId === "string"
                ? store.state.assetxpstate.selectedMarketId.trim()
                : "";
            if (storeSelected && normalizeMarketId(storeSelected) !== dbMarketIdNormalized) {
              var tenantPreference = shouldDeferToTenantStoredMarket(dbMarketId);
              if (tenantPreference.shouldDefer) {
                debugLog("[WorkspaceSync] poll market sync deferred to tenant-stored market", {
                  tenantId: tenantPreference.tenantId,
                  dbMarketId,
                  storeSelectedMarketId: storeSelected,
                  tenantStoredMarketId: tenantPreference.tenantStoredMarketId,
                  tenantStoredMarketUpdatedAtMs:
                    tenantPreference.tenantStoredMarketUpdatedAtMs || null,
                  tenantStoredSelectionIsFresh:
                    !!tenantPreference.tenantStoredSelectionIsFresh,
                });
                return;
              }

              void applyCanonicalMarketContextWithRecovery(
                dbMarketId,
                "poll",
                "assetxp",
                getCurrentTenantIdForMarketSync()
              );
            }
          }
        } else {
          needsMarketListRecovery = true;
        }

        if (needsMarketListRecovery) {
          recordJourneyMetric("journey_market_list_recovery_triggered");
          var marketList =
            store.state.assetxpstate && Array.isArray(store.state.assetxpstate.markets)
              ? store.state.assetxpstate.markets
              : [];
          if (marketList.length === 0) {
            debugLog("[WorkspaceSync] market list empty on in-sync ping — recovering");
            void store.dispatch("assetxpstate/GetMyMarkets");
          }
        }
      }
      return;
    }

    recordJourneyMetric("journey_ping_needs_refresh");

    const hint =
      data.correctTenantId != null
        ? String(data.correctTenantId).trim()
        : data.correct_tenant_id != null
          ? String(data.correct_tenant_id).trim()
          : data.CorrectTenantId != null
            ? String(data.CorrectTenantId).trim()
            : "";
    const authoritativeMarketId = getSelectedMarketIdFromPingBody(data);
    if (authoritativeMarketId) {
      recordJourneyMetric("journey_ping_market_hint_present");
    } else {
      recordJourneyMetric("journey_ping_market_hint_missing");
    }

    // LOOP DETECTION: Check if same token is triggering needsRefresh again
    const tokenPrefix = user.access_token.slice(0, 50);
    const loopThresholdExceeded = checkLoopDetection(tokenPrefix);
    if (loopThresholdExceeded) {
      console.error(
        "[WorkspaceSync] BREAKING INFINITE LOOP: Exceeded consecutive needsRefresh threshold, forcing reload"
      );
      pollCycleLock = false;  // Reset lock before reload
      window.location.reload();
      return;
    }

    // Apply inline token FIRST — must run before jwtTenant===hint skip; otherwise we never
    // replace the Bearer when the server sends the corrected JWT in the ping body.
    var pingToken = getAccessTokenFromPingBody(data);
    var pingExp = getExpiresInFromPingBody(data);

    if (!pingToken && !warnedMissingPingAccessToken) {
      warnedMissingPingAccessToken = true;
      console.warn(
        "[WorkspaceSync] /ping needsRefresh but no access_token parsed — deploy client with getAccessTokenFromPingBody, or fix API JSON keys. Keys:",
        data && typeof data === "object" ? Object.keys(data) : []
      );
    }

    if (pingToken) {
      try {
        debugLog(
          "[WorkspaceSync] /ping returned inline access_token — applying (fast path, no signinSilent)"
        );
        await applyPingInlineAccessToken(
          pingToken,
          pingExp,
          hint || undefined,
          "ping-inline"
        );
        restartPollIntervalAfterTokenUpdate();
      } catch (inlineErr) {
        console.warn(
          "[WorkspaceSync] ping inline token failed, falling back to renew:",
          inlineErr
        );
        recordJourneyMetric("journey_workspace_refresh_triggered");
        await refreshTokenAfterWorkspaceDrift(
          "ping",
          hint || undefined,
          authoritativeMarketId || undefined
        );
      }
      return;
    }

    const jwtTenant = selectedTenantFromAccessTokenJwt(user.access_token);
    const profileTenant =
      user && user.profile && user.profile.selected_tenant != null
        ? String(user.profile.selected_tenant).trim()
        : "";
    const effectiveTenant =
      jwtTenant ||
      profileTenant ||
      getCurrentTenantIdForMarketSync();

    const tokenMarketIdNormalized = normalizeMarketId(
      getSelectedMarketFromToken(user.access_token)
    );
    const profileMarketIdNormalized =
      user && user.profile && typeof user.profile.selected_market === "string"
        ? normalizeMarketId(user.profile.selected_market)
        : "";
    const storeMarketIdNormalized = normalizeMarketId(
      store.state.assetxpstate && typeof store.state.assetxpstate.selectedMarketId === "string"
        ? store.state.assetxpstate.selectedMarketId
        : ""
    );
    const tenantStoredMarketIdNormalized = normalizeMarketId(
      getTenantStoredMarketId(getCurrentTenantIdForMarketSync())
    );
    const effectiveKnownMarketIdNormalized =
      tokenMarketIdNormalized ||
      profileMarketIdNormalized ||
      storeMarketIdNormalized ||
      tenantStoredMarketIdNormalized;
    const pingMarketIdNormalized = normalizeMarketId(authoritativeMarketId);

    // Some server builds can return needsRefresh without an inline access token for market-only
    // drift. If tenant is already aligned, avoid workspace refresh churn and recover market only.
    if (
      authoritativeMarketId &&
      pingMarketIdNormalized &&
      effectiveKnownMarketIdNormalized &&
      pingMarketIdNormalized !== effectiveKnownMarketIdNormalized &&
      effectiveTenant &&
      (!hint || effectiveTenant === hint)
    ) {
      debugLog(
        "[WorkspaceSync] /ping needsRefresh appears market-only — recovering market without workspace refresh",
        {
          jwtTenant: jwtTenant || null,
          profileTenant: profileTenant || null,
          effectiveTenant: effectiveTenant || null,
          hint: hint || null,
          tokenMarketId: tokenMarketIdNormalized || null,
          profileMarketId: profileMarketIdNormalized || null,
          storeMarketId: storeMarketIdNormalized || null,
          tenantStoredMarketId: tenantStoredMarketIdNormalized || null,
          effectiveKnownMarketId: effectiveKnownMarketIdNormalized || null,
          pingMarketId: pingMarketIdNormalized,
        }
      );
      void refreshMarketAfterDrift(authoritativeMarketId);
      return;
    }

    // When tenant is already aligned and /ping provides a market hint, converge to that market
    // even if local effective market is temporarily empty during state churn.
    if (
      authoritativeMarketId &&
      pingMarketIdNormalized &&
      effectiveTenant &&
      (!hint || effectiveTenant === hint)
    ) {
      const availableMarkets =
        store.state.assetxpstate && Array.isArray(store.state.assetxpstate.markets)
          ? store.state.assetxpstate.markets
          : [];
      const marketIsAvailable = availableMarkets.some(
        (m) => m && normalizeMarketId(m.id) === pingMarketIdNormalized
      );

      if (storeMarketIdNormalized !== pingMarketIdNormalized && marketIsAvailable) {
        void applyCanonicalMarketContextWithRecovery(
          authoritativeMarketId,
          "poll",
          "assetxp",
          effectiveTenant
        );
        debugLog("[WorkspaceSync] /ping same-tenant market hint applied directly", {
          effectiveTenant,
          hint: hint || null,
          fromMarketId: storeMarketIdNormalized || null,
          toMarketId: pingMarketIdNormalized,
        });
        return;
      }

      if (!marketIsAvailable) {
        debugLog("[WorkspaceSync] /ping same-tenant market hint not in local list — refreshing market state", {
          effectiveTenant,
          hint: hint || null,
          selectedMarketId: authoritativeMarketId,
          marketCount: availableMarkets.length,
        });
        void refreshMarketAfterDrift(authoritativeMarketId);
        return;
      }
    }

    if (hint && effectiveTenant && effectiveTenant === hint) {
      debugLog(
        "[WorkspaceSync] /ping needsRefresh but selected_tenant already matches DB — skipping renew",
        {
          jwtTenant: jwtTenant || null,
          profileTenant: profileTenant || null,
          effectiveTenant,
          hint,
        }
      );
      return;
    }

    debugLog(
      "[WorkspaceSync] /ping out of sync, no inline token — renew (signinSilent / adopt)",
      data
    );
      recordJourneyMetric("journey_workspace_refresh_triggered");
      await refreshTokenAfterWorkspaceDrift(
      "ping",
      hint || undefined,
      authoritativeMarketId || undefined
    );
  } catch (e) {
    console.warn("[WorkspaceSync] Workspace ping failed:", e);
  } finally {
    pollCycleLock = false;
  }
}

function ensureBroadcastChannel() {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!broadcastChannel) {
    broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    broadcastChannel.onmessage = onBroadcastMessage;
  }
  return broadcastChannel;
}

/**
 * Notify other tabs/apps after a switch in this app.
 */
export function notifyWorkspaceSwitchAcrossApps(result) {
  const tenantId = result.tenantId != null ? result.tenantId : result.tenant_id;
  const accessToken =
    (result.access_token != null ? result.access_token : result.accessToken) || null;
  const expiresIn =
    (result.expires_in != null ? result.expires_in : result.expiresIn) || null;

  const payload = {
    type: "workspace-switched",
    tenantId,
    access_token: accessToken,
    expires_in: expiresIn,
    timestamp: Date.now(),
  };

  try {
    const ch = ensureBroadcastChannel();
    if (ch) {
      ch.postMessage(payload);
    }
  } catch (e) {
    console.warn("[WorkspaceSync] BroadcastChannel post failed:", e);
  }

  try {
    if (window.localStorage) {
      localStorage.setItem(STORAGE_KEY_SWITCHED, JSON.stringify(payload));
      setTimeout(() => {
        try {
          localStorage.removeItem(STORAGE_KEY_SWITCHED);
        } catch (ignore) {
          /* empty */
        }
      }, 500);
      if (tenantId != null) {
        localStorage.setItem(
          "workspace_changed",
          JSON.stringify({ tenantId, timestamp: Date.now() })
        );
      }
    }
  } catch (e) {
    console.warn("[WorkspaceSync] localStorage notify failed:", e);
  }
}

/**
 * Skip redundant cross-tab refresh only when **both** OIDC profile and workspace UI state
 * already match the broadcast tenant. Using profile alone was wrong: id_token could update
 * before WorkspaceSwitcher ran loadWorkspaces, so we ignored the broadcast and the dropdown
 * stayed on the old tenant.
 */
function shouldIgnoreBroadcastTenant(switchTenantId) {
  if (switchTenantId == null) return false;
  const sw = String(switchTenantId);
  const p = store.state.authentication.user && store.state.authentication.user.profile;
  const profileTenant =
    p && p.selected_tenant != null ? String(p.selected_tenant) : null;
  const workspaceTenant =
    store.state.workspace && store.state.workspace.currentTenantId != null
      ? String(store.state.workspace.currentTenantId)
      : null;
  if (profileTenant == null || workspaceTenant == null) return false;
  return profileTenant === sw && workspaceTenant === sw;
}

function onBroadcastMessage(event) {
  const data = event && event.data;
  // Accept payload with or without type field (Portal doesn't include type)
  if (!data) return;
  
  // Handle workspace-switched (with or without type field)
  if (data.tenantId) {
    if (shouldIgnoreBroadcastTenant(data.tenantId)) return;
    debugLog("[WorkspaceSync] BroadcastChannel workspace-switched");
    refreshTokenAfterWorkspaceDrift(
      "broadcast",
      data.tenantId != null ? String(data.tenantId) : undefined
    );
  }
  
  // Handle market-switched
  if (data.type === "market-switched" && data.marketId) {
    recordJourneyMetric("journey_ingress_market_broadcast");
    debugLog("[WorkspaceSync] BroadcastChannel market-switched:", data.marketId);
    var broadcastTenantId =
      data.tenantId != null ? String(data.tenantId).trim() : "";
    var currentTenantId = getCurrentTenantIdForMarketSync();
    if (broadcastTenantId && currentTenantId && broadcastTenantId !== currentTenantId) {
      debugLog("[WorkspaceSync] ignoring market-switched broadcast for different tenant", {
        currentTenantId,
        eventTenantId: broadcastTenantId,
        marketId: data.marketId,
      });
      return;
    }
    // Phase 1 architecture: treat cross-app market event as hint only.
    // Reconcile from canonical /ping instead of applying event payload directly.
    void pollWorkspacePing();
  }
}

function onStorageEvent(event) {
  if (process.env.NODE_ENV === 'development') {
    debugLog("[WorkspaceSync] storage event fired:", { key: event.key, hasNewValue: !!event.newValue });
  }
  
  if (!event.newValue) return;

  // Handle primary key (ixp-workspace-switched) and old key (workspace-switched) for cross-app sync
  if (event.key === STORAGE_KEY_SWITCHED || event.key === STORAGE_KEY_SWITCHED_OLD) {
    try {
      const data = JSON.parse(event.newValue);
      // Accept payload with or without type field (Portal doesn't include type)
      if (!data || !data.tenantId) return;
      if (Date.now() - (data.timestamp || 0) > STORAGE_EVENT_MAX_AGE_MS) return;
      if (shouldIgnoreBroadcastTenant(data.tenantId)) return;
      debugLog("[WorkspaceSync] storage ixp-workspace-switched (other app/tab)");
      refreshTokenAfterWorkspaceDrift(
        "storage",
        data.tenantId != null ? String(data.tenantId) : undefined
      );
    } catch (e) {
      console.warn("[WorkspaceSync] storage parse failed:", e);
    }
    return;
  }

  if (event.key === "workspace_changed") {
    try {
      const data = JSON.parse(event.newValue);
      if (Date.now() - (data.timestamp || 0) > STORAGE_EVENT_MAX_AGE_MS) return;
      if (shouldIgnoreBroadcastTenant(data.tenantId)) return;
      debugLog("[WorkspaceSync] storage workspace_changed (legacy)");
      refreshTokenAfterWorkspaceDrift(
        "workspace_changed",
        data.tenantId != null ? String(data.tenantId) : undefined
      );
    } catch (e) {
      console.warn("[WorkspaceSync] workspace_changed parse failed:", e);
    }
    return;
  }

  // Handle market-switched via localStorage (cross-origin sync with Online Portal)
  if (event.key === "ixp-market-switched") {
    try {
      const data = JSON.parse(event.newValue);
      if (!data || !data.marketId) return;
      if (Date.now() - (data.timestamp || 0) > STORAGE_EVENT_MAX_AGE_MS) return;

      var storageTenantId =
        data.tenantId != null ? String(data.tenantId).trim() : "";
      var currentTenantId = getCurrentTenantIdForMarketSync();
      if (storageTenantId && currentTenantId && storageTenantId !== currentTenantId) {
        debugLog("[WorkspaceSync] ignoring market-switched storage hint for different tenant", {
          currentTenantId,
          eventTenantId: storageTenantId,
          marketId: data.marketId,
        });
        return;
      }

      recordJourneyMetric("journey_ingress_market_storage");
      
      debugLog("[WorkspaceSync] storage ixp-market-switched hint received");
      // Phase 1 architecture: treat cross-app market event as hint only.
      // Reconcile from canonical /ping instead of applying event payload directly.
      void pollWorkspacePing();
    } catch (e) {
      console.warn("[WorkspaceSync] ixp-market-switched parse failed:", e);
    }
  }
}

export function initWorkspaceCrossAppListeners() {
  try {
    ensureBroadcastChannel();
  } catch (e) {
    console.warn("[WorkspaceSync] BroadcastChannel listen failed:", e);
  }

  if (!storageListenerBound && window.addEventListener) {
    window.addEventListener("storage", onStorageEvent);
    storageListenerBound = true;
  }

  if (!visibilityListenerBound && typeof document !== "undefined" && document.addEventListener) {
    document.addEventListener("visibilitychange", onDocumentVisibilityChange);
    visibilityListenerBound = true;
  }
}

/** When user returns to this tab, ping immediately — cross-origin apps cannot use BroadcastChannel. */
function onDocumentVisibilityChange() {
  if (typeof document === "undefined") return;
  if (document.visibilityState !== "visible") return;
  if (offlineConfig.isOfflineMode && offlineConfig.bypassAuth) return;
  if (!store.state.authentication.loggedIn) return;
  pollWorkspacePing();
}

export async function startWorkspacePingPolling(intervalMs) {
  stopWorkspacePingPolling();
  if (offlineConfig.isOfflineMode && offlineConfig.bypassAuth) return;

  await initMonitorCurrentUserFromStorage();

  const ms =
    typeof intervalMs === "number" && intervalMs >= 2000
      ? intervalMs
      : DEFAULT_POLL_MS;

  activePollIntervalMs = ms;

  pollWorkspacePing();
  pollTimerId = window.setInterval(pollWorkspacePing, ms);
}

async function initMonitorCurrentUserFromStorage() {
  const userManager = getUserManager();
  if (!userManager || typeof userManager.getUser !== "function") return;
  try {
    monitorCurrentUser = await userManager.getUser();
  } catch (e) {
    console.warn("[WorkspaceSync] init monitor user failed:", e);
  }
}

export function stopWorkspacePingPolling() {
  if (pollTimerId != null) {
    clearInterval(pollTimerId);
    pollTimerId = null;
  }
}

/**
 * Start listeners + ping polling when user is logged in.
 * @param {{ pollIntervalMs?: number }} [options]
 * @returns {Promise<void>}
 */
export async function startWorkspaceCrossAppSync(options) {
  initWorkspaceCrossAppListeners();
  if (pollTimerId != null) {
    return;
  }
  const envMs = parseInt(process.env.VUE_APP_WORKSPACE_POLL_MS, 10);
  const intervalMs =
    options && options.pollIntervalMs != null
      ? options.pollIntervalMs
      : !isNaN(envMs) && envMs >= 2000
        ? envMs
        : undefined;
  await startWorkspacePingPolling(intervalMs);
}

export function stopWorkspaceCrossAppSync() {
  monitorCurrentUser = null;
  stopWorkspacePingPolling();
}

/**
 * Call after `applyNewTokenFromWorkspaceSwitch` so /ping uses the new JWT without waiting for getUser.
 * @param {import("oidc-client").User | null} user
 */
export function setMonitorCurrentUser(user) {
  monitorCurrentUser = user || null;
}

/** @returns {import("oidc-client").User | null} */
export function getMonitorCurrentUser() {
  return monitorCurrentUser || null;
}

/** Prefer for axios (before UserManager.getUser) when oidc storage lags monitor in-memory user. */
export function getMonitorCurrentUserAccessToken() {
  return monitorCurrentUser && monitorCurrentUser.access_token
    ? monitorCurrentUser.access_token
    : null;
}
